import { useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Loader2, Mail, MapPin, Phone, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Icon } from "@/components/common/Icon";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { PageHero, CtaBand } from "@/components/site/PageHero";
import { SelectField, TextAreaField, TextField } from "@/components/common/Forms";
import { SuccessPanel } from "@/components/common/SuccessPanel";
import { COMPLIANCE, CONTACT, IMAGES, INDUSTRIES, STANDARDS_REVIEWED } from "@/data/site";

const VALUES = [
  ["Readiness over rhetoric", "We measure success by systems that work during a drill, an audit and an actual incident."],
  ["One accountable partner", "Supply, installation and AMC under a single team, with no finger-pointing between vendors."],
  ["Discretion by design", "Site layouts, risk reports and BOQs stay confidential. We never publish client system details."],
];

export function About() {
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="About Vinsafe" title="A fire safety partner built for enterprise accountability." text="Vinsafe, a part of Vingrace Solutions Private Limited, brings product supply, wall-mounting, alarm wiring and AMC together so facility and procurement teams manage one relationship, not six vendors." image={IMAGES.corporate} />
      <section className="container-x grid gap-12 py-20 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeading eyebrow="Our approach" title="Consultative. Standards-led. Quietly thorough." text="Most fire safety requirements are mixed, bulk and compliance-driven. That's why we don't sell through a checkout — we survey, specify and stay on as your maintenance partner." />
          <div className="mt-10 space-y-6">
            {VALUES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08} className="flex gap-5">
                <span className="mono text-sm font-semibold text-[#C20500]">0{i + 1}</span>
                <div><h3 className="text-lg font-semibold text-slate-900">{t}</h3><p className="mt-1 text-slate-600">{d}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative h-[460px] overflow-hidden rounded-2xl">
          <img src={IMAGES.training} alt="Fire safety training" className="h-full w-full object-cover" />
          <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/20 bg-[#123634]/80 p-6 text-white backdrop-blur-xl">
            <p className="mono text-[11px] uppercase tracking-[0.18em] text-[#E8927F]">Pan-regional service</p>
            <p className="mt-2 text-lg font-semibold">Technicians, auditors and trainers deployed across metro and tier-2 cities.</p>
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}

export function Industries() {
  return (
    <div data-testid="industries-page">
      <PageHero eyebrow="Industries served" title="Sector-specific protection for complex occupancies." text="Each sector carries a distinct risk profile and regulator. We map systems and maintenance to both." image={IMAGES.highrise} />
      <section className="container-x grid gap-5 py-20 md:grid-cols-2">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.name} delay={(i % 2) * 0.08}>
            <div className="card-base flex h-full gap-5 p-8" data-testid={`industry-card-${i}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#123634]"><Icon name={ind.icon} className="h-5 w-5 text-white" /></span>
              <div>
                <h3 className="text-xl font-semibold text-slate-900">{ind.name}</h3>
                <p className="mt-3 text-sm text-slate-600"><span className="mono text-[11px] uppercase tracking-wider text-slate-400">Risk · </span>{ind.risk}</p>
                <p className="mt-1.5 text-sm text-slate-600"><span className="mono text-[11px] uppercase tracking-wider text-slate-400">Fit · </span>{ind.fit}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>
      <CtaBand />
    </div>
  );
}

export function Compliance() {
  return (
    <div data-testid="compliance-page">
      <PageHero eyebrow="Compliance & certification" title="Every recommendation traces back to a standard." text="We design, supply and maintain to recognised Indian and international fire codes, and document it so your audit trail is always complete." image={IMAGES.technician} />
      <section className="container-x py-20">
        <p className="mb-8 text-sm text-slate-500" data-testid="compliance-reviewed-note">Standard editions last reviewed: <span className="font-semibold text-slate-800">{STANDARDS_REVIEWED}</span>. Local fire authority and state byelaw requirements apply in addition.</p>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {COMPLIANCE.map((c, i) => (
            <Reveal key={c.code} delay={(i % 3) * 0.08}>
              <div className="card-base h-full p-8" data-testid={`compliance-card-${i}`}>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800"><BadgeCheck className="h-3.5 w-3.5" /> {c.code}</span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex flex-col gap-4 rounded-xl border border-slate-200 bg-[#F4F6F2] p-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-4">
            <Lock className="h-6 w-6 shrink-0 text-slate-700" />
            <div>
              <p className="font-semibold text-slate-900">Certificates & test reports on request</p>
              <p className="text-sm text-slate-600">Available to verified buyers and approved portal vendors during evaluation or audit.</p>
            </div>
          </div>
          <Link to="/contact" className="btn-dark shrink-0" data-testid="compliance-request-docs-button">Request documentation</Link>
        </div>
      </section>
    </div>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...f, kind: "contact" });
      setDone(data.reference);
      toast.success("Message sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Talk to a fire safety specialist." text="Product, AMC or documentation requests: we route every message to the right team." image={IMAGES.atrium} />
      <section className="container-x grid gap-10 py-16 lg:grid-cols-3">
        <div className="space-y-4">
          {[[Phone, "Phone / WhatsApp", CONTACT.phone], [Mail, "Email", CONTACT.email], [Clock, "Coverage", CONTACT.support], [MapPin, "Head office", CONTACT.address]].map(([I, t, v]) => (
            <div key={t} className="card-base flex gap-4 p-5"><I className="h-5 w-5 shrink-0 text-[#C20500]" /><div><p className="mono text-[11px] uppercase tracking-wider text-slate-500">{t}</p><p className="mt-1 text-sm font-medium text-slate-900">{v}</p></div></div>
          ))}
        </div>
        <div className="lg:col-span-2">
          {done ? (
            <SuccessPanel title="Message received" text="We'll get back to you within one business day." reference={done} actions={[{ to: "/", label: "Back to home" }]} testid="contact-success" />
          ) : (
            <form onSubmit={submit} className="card-base grid gap-5 p-6 sm:grid-cols-2 sm:p-10" data-testid="contact-form">
              <TextField id="contact-name" label="Full name" value={f.name} onChange={set("name")} required />
              <TextField id="contact-company" label="Company" value={f.company} onChange={set("company")} />
              <TextField id="contact-email" type="email" label="Email" value={f.email} onChange={set("email")} required />
              <TextField id="contact-phone" label="Phone" value={f.phone} onChange={set("phone")} required />
              <SelectField id="contact-subject" label="Topic" value={f.subject} onChange={set("subject")} options={["Product enquiry", "AMC enquiry", "Documentation request", "Other"]} className="sm:col-span-2" />
              <TextAreaField id="contact-message" label="Message" value={f.message} onChange={set("message")} required className="sm:col-span-2" />
              <button type="submit" disabled={busy} className="btn-primary sm:col-span-2 sm:w-fit" data-testid="contact-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send message</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
